import { jwtDecode } from 'jwt-decode';
import { apiClient } from './api';
import { tokenManager } from './tokenManager';

/**
 * PKCE (Proof Key for Code Exchange) parameters
 */
interface PKCEParams {
  codeVerifier: string;
  codeChallenge: string;
}

/**
 * OAuth callback parameters
 */
interface CallbackParams {
  code: string;
  state: string;
}

/**
 * Token response from Cognito
 */
interface TokenResponse {
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

/**
 * Decoded ID token claims
 */
interface IdTokenClaims {
  sub: string;
  email: string;
  name?: string;
  'cognito:groups'?: string[];
  nonce?: string;
  iss: string;
  aud: string;
  exp: number;
  iat: number;
}

/**
 * AuthService handles OAuth 2.0 authentication with Cognito + IAM Identity Center
 * 
 * Implements:
 * - PKCE (Proof Key for Code Exchange) for security
 * - State parameter for CSRF protection
 * - Nonce for replay protection
 * - JWT validation
 */
export class AuthService {
  private cognitoDomain: string;
  private clientId: string;
  private redirectUri: string;
  private logoutUri: string;

  constructor() {
    this.cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN || '';
    this.clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || '';
    this.redirectUri = import.meta.env.VITE_COGNITO_REDIRECT_URI || '';
    this.logoutUri = import.meta.env.VITE_COGNITO_LOGOUT_URI || '';
  }

  /**
   * Generate PKCE parameters for OAuth authorization code flow
   * 
   * Creates a code_verifier (random string) and code_challenge (SHA256 hash)
   * as per RFC 7636
   */
  async generatePKCE(): Promise<PKCEParams> {
    // Generate random code_verifier (43-128 characters)
    const codeVerifier = this.generateRandomString(43);

    // Create code_challenge using SHA256
    const codeChallenge = await this.sha256(codeVerifier);

    return {
      codeVerifier,
      codeChallenge,
    };
  }

  /**
   * Generate cryptographically secure random string
   */
  private generateRandomString(length: number): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    const randomValues = new Uint8Array(length);
    crypto.getRandomValues(randomValues);

    let result = '';
    for (let i = 0; i < length; i++) {
      const value = randomValues[i];
      if (value !== undefined) {
        result += charset[value % charset.length];
      }
    }

    return result;
  }

  /**
   * Create SHA256 hash and encode as base64url
   */
  private async sha256(plain: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(plain);
    const hash = await crypto.subtle.digest('SHA-256', data);

    // Convert to base64url
    return this.base64UrlEncode(hash);
  }

  /**
   * Base64url encode (without padding)
   */
  private base64UrlEncode(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      const byte = bytes[i];
      if (byte !== undefined) {
        binary += String.fromCharCode(byte);
      }
    }

    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Initiate login flow - redirect to Cognito Hosted UI
   * 
   * Generates PKCE parameters, state, and nonce for security
   */
  async login(): Promise<void> {
    console.log('[Auth] Login initiated', {
      timestamp: new Date().toISOString(),
      cognitoDomain: this.cognitoDomain,
      clientId: this.clientId,
    });

    // Generate PKCE parameters
    const { codeVerifier, codeChallenge } = await this.generatePKCE();

    // Generate state for CSRF protection
    const state = this.generateRandomString(32);

    // Generate nonce for replay protection
    const nonce = this.generateRandomString(32);

    // Store parameters in sessionStorage for callback
    sessionStorage.setItem('pkce_code_verifier', codeVerifier);
    sessionStorage.setItem('oauth_state', state);
    sessionStorage.setItem('oauth_nonce', nonce);

    // Build authorization URL
    // Note: cognitoDomain already includes https:// from environment variable
    const authUrl = new URL(`${this.cognitoDomain}/oauth2/authorize`);
    authUrl.searchParams.set('client_id', this.clientId);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', 'openid email profile');
    authUrl.searchParams.set('redirect_uri', this.redirectUri);
    authUrl.searchParams.set('code_challenge', codeChallenge);
    authUrl.searchParams.set('code_challenge_method', 'S256');
    authUrl.searchParams.set('state', state);
    authUrl.searchParams.set('nonce', nonce);
    authUrl.searchParams.set('identity_provider', 'IAMIdentityCenter');

    console.log('[Auth] Redirecting to Cognito Hosted UI', {
      timestamp: new Date().toISOString(),
      redirectUri: this.redirectUri,
    });

    // Redirect to Cognito
    window.location.href = authUrl.toString();
  }

  /**
   * Handle OAuth callback from Cognito
   * 
   * Validates state parameter and exchanges authorization code for tokens
   */
  async handleCallback(): Promise<TokenResponse> {
    console.log('[Auth] Handling OAuth callback', {
      timestamp: new Date().toISOString(),
    });

    // Extract parameters from URL
    const params = this.extractCallbackParams();

    // Validate state parameter (CSRF protection)
    this.validateState(params.state);

    // Get stored PKCE verifier
    const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
    if (!codeVerifier) {
      console.error('[Auth] PKCE code verifier not found', {
        timestamp: new Date().toISOString(),
      });
      throw new Error('PKCE code verifier not found');
    }

    // Exchange authorization code for tokens
    const tokens = await this.exchangeCodeForTokens(params.code, codeVerifier);

    // Validate ID token
    const claims = await this.validateIdToken(tokens.id_token);

    console.log('[Auth] Authentication successful', {
      timestamp: new Date().toISOString(),
      userId: claims.sub,
      email: claims.email,
      name: claims.name,
    });

    // Generate CSRF token for API requests
    apiClient.generateCsrfToken();

    // Clean up session storage
    sessionStorage.removeItem('pkce_code_verifier');
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('oauth_nonce');

    return tokens;
  }

  /**
   * Extract callback parameters from URL
   */
  private extractCallbackParams(): CallbackParams {
    const urlParams = new URLSearchParams(window.location.search);

    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    if (error) {
      throw new Error(`OAuth error: ${error} - ${errorDescription || 'Unknown error'}`);
    }

    if (!code || !state) {
      throw new Error('Missing required callback parameters');
    }

    return { code, state };
  }

  /**
   * Validate state parameter to prevent CSRF attacks
   */
  private validateState(state: string): void {
    const storedState = sessionStorage.getItem('oauth_state');

    if (!storedState) {
      throw new Error('OAuth state not found');
    }

    if (state !== storedState) {
      throw new Error('Invalid OAuth state - possible CSRF attack');
    }
  }

  /**
   * Exchange authorization code for tokens using PKCE
   */
  async exchangeCodeForTokens(code: string, codeVerifier: string): Promise<TokenResponse> {
    const tokenUrl = `${this.cognitoDomain}/oauth2/token`;

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: this.clientId,
      code,
      redirect_uri: this.redirectUri,
      code_verifier: codeVerifier,
    });

    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body.toString(),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Token exchange failed: ${error}`);
    }

    return response.json();
  }

  /**
   * Validate ID token JWT
   * 
   * Verifies:
   * - Token structure
   * - Nonce (replay protection)
   * - Issuer
   * - Audience
   * - Expiration
   * - Issued-at time
   */
  async validateIdToken(idToken: string): Promise<IdTokenClaims> {
    try {
      // Decode token (note: this doesn't verify signature)
      const claims = jwtDecode<IdTokenClaims>(idToken);

      // Validate nonce
      const storedNonce = sessionStorage.getItem('oauth_nonce');
      if (storedNonce && claims.nonce !== storedNonce) {
        throw new Error('Invalid nonce - possible replay attack');
      }

      // Validate issuer
      const expectedIssuer = `https://cognito-idp.${import.meta.env.VITE_AWS_REGION || 'eu-west-2'}.amazonaws.com/${import.meta.env.VITE_COGNITO_USER_POOL_ID || ''}`;
      if (claims.iss !== expectedIssuer) {
        throw new Error(`Invalid issuer: ${claims.iss}`);
      }

      // Validate audience
      if (claims.aud !== this.clientId) {
        throw new Error(`Invalid audience: ${claims.aud}`);
      }

      // Validate expiration
      const now = Math.floor(Date.now() / 1000);
      if (claims.exp < now) {
        throw new Error('Token has expired');
      }

      // Validate issued-at time (not in future)
      if (claims.iat > now + 60) {
        // Allow 60 seconds clock skew
        throw new Error('Token issued in the future');
      }

      return claims;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`ID token validation failed: ${error.message}`);
      }
      throw new Error('ID token validation failed');
    }
  }

  /**
   * Logout user and redirect to Cognito logout endpoint
   */
  logout(): void {
    console.log('[Auth] Logout initiated', {
      timestamp: new Date().toISOString(),
    });

    // Clear CSRF token
    apiClient.clearCsrfToken();

    // Clear all stored tokens (cookies and sessionStorage)
    tokenManager.clearTokens();

    // Clear all sessionStorage (including PKCE params, state, etc.)
    sessionStorage.clear();

    // Clear localStorage as well (in case anything is stored there)
    localStorage.clear();

    console.log('[Auth] Local session cleared', {
      timestamp: new Date().toISOString(),
    });

    // Build logout URL to clear Cognito session
    const logoutUrl = new URL(`${this.cognitoDomain}/logout`);
    logoutUrl.searchParams.set('client_id', this.clientId);
    logoutUrl.searchParams.set('logout_uri', this.logoutUri);

    console.log('[Auth] Redirecting to Cognito logout', {
      timestamp: new Date().toISOString(),
      logoutUri: this.logoutUri,
    });

    // Redirect to Cognito logout (which will redirect back to logoutUri)
    window.location.href = logoutUrl.toString();
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(refreshToken: string): Promise<TokenResponse> {
    console.log('[Auth] Token refresh initiated', {
      timestamp: new Date().toISOString(),
    });

    const tokenUrl = `${this.cognitoDomain}/oauth2/token`;

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: this.clientId,
      refresh_token: refreshToken,
    });

    try {
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: body.toString(),
      });

      if (!response.ok) {
        const error = await response.text();
        console.error('[Auth] Token refresh failed', {
          timestamp: new Date().toISOString(),
          status: response.status,
          error,
        });
        throw new Error(`Token refresh failed: ${error}`);
      }

      const tokens = await response.json();

      console.log('[Auth] Token refresh successful', {
        timestamp: new Date().toISOString(),
        expiresIn: tokens.expires_in,
      });

      return tokens;
    } catch (error) {
      console.error('[Auth] Token refresh error', {
        timestamp: new Date().toISOString(),
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
